import { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

interface PairSliderPictureProps {
  src: string;
  clipPath?: string;
  smoothTransition?: boolean;
}

function getResizedImageUrl(imageUrl: string, minWidth: number | null) {
  const { hostname } = new URL(imageUrl);

  if (!minWidth || hostname !== 'files.elfsight.com') {
    return imageUrl;
  }

  return imageUrl;
}

export function PairSliderPicture({
  src,
  clipPath,
  smoothTransition = false
}: PairSliderPictureProps) {
  const imageRef = useRef<HTMLDivElement>(null);

  const [imageSize, setImageSize] = useState<[number | null, number | null]>([
    null,
    null
  ]);
  const [imageWidth, imageHeight] = imageSize;

  let containerInlineStyle;

  if (clipPath) {
    containerInlineStyle = {
      clipPath,
      WebkitClipPath: clipPath
    };
  }

  useLayoutEffect(() => {
    const { current: imageElement } = imageRef;

    if (!imageElement) {
      return;
    }

    const { width: nextImageWidth, height: nextImageHeight } =
      imageElement.getBoundingClientRect();

    if (nextImageWidth !== imageWidth || nextImageHeight !== imageHeight) {
      setImageSize([nextImageWidth, nextImageHeight]);
    }
  });

  return (
    <Container
      style={containerInlineStyle}
      _smoothTransition={smoothTransition}
    >
      {src ? (
        <Image
          ref={imageRef}
          _elementBackgroundImage={getResizedImageUrl(src, imageWidth)}
        />
      ) : null}
    </Container>
  );
}

const Container = styled.div<{
  _smoothTransition: boolean;
}>`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  ${({ _smoothTransition }) =>
    _smoothTransition &&
    css`
      transition-property:
        clip-path,
        -webkit-clip-path;
      transition-duration: 0.4s;
      transition-timing-function: ease;
    `};
`;

const Image = styled.div<{
  _elementBackgroundImage: string;
}>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;

  ${({ _elementBackgroundImage }) =>
    _elementBackgroundImage &&
    css`
      background-image: url('${_elementBackgroundImage}');
    `};
`;
